import { InputGroup } from '@blueprintjs/core';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import ErrorMessage from 'Components/Generic/Misc/ErrorMessage';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import ResourceDrawerButton from 'Components/SiteDrawerContainer/ResourceDrawerButton';
import { useAlertContext } from 'Data/context/Alerts';
import ResourceData from 'Data/ResourceData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { areEquivalent } from 'Util';
import styles from './Resources.module.css';

let DM = new DataManager();
const ResourcesTable = ({
  orgId,
  editable = false,
  orgOptionNotSet = false,
  onUpdateResources = () => {},
}) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const [resourceList, setResourceList] = useState([]);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const orgIdRef = useRef();

  useEffect(() => {
    DM.add(
      ResourceData.registerListener(
        'RES_LIST_FOR_CORE_LOADED',
        onResourceListLoaded,
      ),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener(
        'RECORDS_LOAD_ERROR',
        onResourceListLoadError,
      ),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener(
        'BLANK_RECORD_LOADED',
        onBlankResourceLoaded,
      ),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORDS_UPDATED', onResourcesUpdated),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORDS_DELETED', onResourceDeleted),
      ResourceData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    orgIdRef.current = orgId;
    fetchResources();
  }, [orgId]);

  const fetchResources = () => {
    if (orgIdRef.current) {
      ResourceData.getResourcesForOrg(orgIdRef.current);
    } else {
      setResourceList([]);
    }
  };

  const onResourceListLoaded = (payload) => {
    if (payload) {
      setResourceList(payload.records);
      setHasBeenUpdated(false);
    }
  };

  const onResourceListLoadError = () => {};

  const onBlankResourceLoaded = (newResource) => {
    setHasBeenUpdated(true);
    let updatedLineItem = {
      ...newResource,
      parent_organization_id: orgIdRef.current,
    };
    setResourceList((prevResourceList) => [
      ...prevResourceList,
      updatedLineItem,
    ]);
  };

  const onResourcesUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Resources Saved');
      fetchResources();
      onUpdateResources();
    }
  };

  const updateItemValue = (itemToUpdate, updateField, updatedValue) => {
    const updatedArray = resourceList.map((item) => {
      if (item === itemToUpdate) {
        let originalItem = { ...item };
        let updatedItem = { ...item, [updateField]: updatedValue };
        if (!areEquivalent(originalItem, updatedItem)) {
          setHasBeenUpdated(true);
        }
        return updatedItem;
      }
      return item;
    });
    setResourceList(updatedArray);
  };

  const saveClicked = () => {
    ResourceData.updateOrCreateRecords(resourceList);
  };

  const deleteResource = (resource) => {
    ResourceData.deleteRecords([resource.id]);
  };

  const deleteClicked = (resource) => {
    if (resource.id) {
      openDeleteDialog({
        objectType: 'Resource',
        onConfirmDelete: () => deleteResource(resource),
      });
    } else {
      setResourceList((prevResources) =>
        prevResources.filter((item) => item !== resource),
      );
    }
  };

  const onResourceDeleted = () => {
    showDeletedToast('Resource');
    fetchResources();
  };

  const undoChanges = () => {
    fetchResources();
  };

  let headers = (
    <tr>
      <td style={{ width: '25%' }} className={styles.table_header_cell}>
        Name
      </td>
      <td style={{ width: '50%' }} className={styles.table_header_cell}>
        Description
      </td>
      <td style={{ width: '30%' }} className={styles.table_header_cell}>
        Inventory Tag #
      </td>
      {editable && <td style={{ width: 20 }}></td>}
    </tr>
  );

  let colSpan = 3 + (editable ? 1 : 0);
  let resourceListContent =
    !resourceList ||
    (resourceList.length === 0 && (
      <tr>
        <td colSpan={colSpan}>
          <NoContentBox label={'No Resources'} />
        </td>
      </tr>
    )) ||
    resourceList.map((resource, index) => {
      return (
        <tr key={index}>
          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={resource.name || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(resource, 'name', event.target.value);
                }}
              />
            )}
            {!editable && <ResourceDrawerButton resource={resource} />}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={resource.description || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(resource, 'description', event.target.value);
                }}
              />
            )}
            {!editable && <span>{resource.description || ''}</span>}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={resource.inventory_tag_number || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(
                    resource,
                    'inventory_tag_number',
                    event.target.value,
                  );
                }}
              />
            )}
            {!editable && <span>{resource.inventory_tag_number || ''}</span>}
          </td>

          {editable && (
            <td>
              <DeleteButton
                miniVersion
                onDeleteItem={() => deleteClicked(resource)}
                tooltipText={'Delete Resource'}
              />
            </td>
          )}
        </tr>
      );
    });

  const newButton = editable && (
    <AddNewButton
      clickHandler={ResourceData.getBlank}
      buttonText='Add a Resource/Asset'
    />
  );

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={saveClicked} />
  );

  const footer = (
    <ButtonFooter marginTop={0}>
      {undoButton}
      {newButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <div style={{ minWidth: 500 }}>
      {orgOptionNotSet && (
        <ErrorMessage
          small
          message={'Warning: Core facility is not set up to track resources.'}
        />
      )}
      <table className={styles.resource_list_table}>
        <thead>{headers}</thead>
        <tbody>{resourceListContent}</tbody>
      </table>
      {footer}
    </div>
  );
};

ResourcesTable.propTypes = {
  orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editable: PropTypes.bool,
  orgOptionNotSet: PropTypes.bool,
  onUpdateResources: PropTypes.func,
};

export default ResourcesTable;
